"use client";

import { useState } from "react";

import { addToast } from "@heroui/toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldValues, useForm } from "react-hook-form";
import { z } from "zod";

import { EventKeyValues } from "@/lib/observability/catalog";
import { EventPublisher } from "@/lib/observability/events";
import { isNetworkErrorMessage } from "@/lib/observability/expected-errors";
import {
  ActionResponse,
  isErrorResponse,
  isSuccessResponse,
} from "@/lib/utils/errors";

/**
 * Options for the useFormSubmission hook
 */
interface UseFormSubmissionOptions<TData, TResult> {
  /**
   * The function that submits the form data
   */
  onSubmit: (data: TData) => Promise<TResult>;

  /**
   * Called when submission is successful
   */
  onSuccess?: (result: TResult) => void;

  /**
   * Called when submission throws an unexpected error
   */
  onError?: (error: Error) => void;

  /**
   * Whether to reset the error state when submitting
   * @default true
   */
  resetErrorOnSubmit?: boolean;

  /**
   * Called before submission
   */
  onBeforeSubmit?: () => void;

  /**
   * Show toast on error/success
   * @default true
   */
  showToast?: boolean;

  /**
   * Event key for observability tracking
   */
  eventKeys: {
    success: EventKeyValues;
    failure: EventKeyValues;
    attempt: EventKeyValues;
  };

  /**
   * Additional data to include in observability events
   */
  eventData?: Record<string, any>;
}

/**
 * Result of the useFormSubmission hook
 */
interface UseFormSubmissionResult<TData, TResult> {
  /**
   * Whether the form is currently submitting
   */
  isSubmitting: boolean;

  /**
   * The error message, if any
   */
  error: string | null;

  /**
   * Function to handle form submission
   */
  handleSubmit: (data: TData) => Promise<TResult | undefined>;

  /**
   * Set the error message
   */
  setError: (error: string | null) => void;

  /**
   * Clear the error message
   */
  clearError: () => void;
}

/**
 * Hook to standardize form submission handling
 *
 * This hook now automatically handles ActionResponse structures from server actions.
 * Your onSubmit function should return an ActionResponse, and this hook will:
 * - Check if the response is successful or an error
 * - Extract error messages automatically
 * - Show appropriate toasts
 * - Call success/error callbacks
 *
 * @example
 * ```tsx
 * const { isSubmitting, error, handleSubmit } = useFormSubmission({
 *   onSubmit: (data) => createUser(data), // Returns ActionResponse<User>
 *   onSuccess: () => router.push('/users'),
 *   onError: (err) => console.error(err),
 * });
 * ```
 */
interface SubmissionContext<TData> {
  data: TData;
  eventData: Record<string, any>;
  eventKeys: {
    success: EventKeyValues;
    failure: EventKeyValues;
    attempt: EventKeyValues;
  };
  showToast: boolean;
  setError: (msg: string | null) => void;
  setIsSubmitting: (val: boolean) => void;
  onError?: (error: Error) => void;
  onSuccess?: (result: any) => void;
}

function publishFailureEvent(
  eventKeys: { failure: EventKeyValues },
  eventData: Record<string, any>,
  data: unknown,
  errorMessage: string,
): void {
  if (eventKeys.failure) {
    void EventPublisher.publishEvent(eventKeys.failure, {
      ...eventData,
      error: errorMessage,
      form_data: data,
    });
  }
}

function publishSuccessEvent(
  eventKeys: { success: EventKeyValues },
  eventData: Record<string, any>,
  data: unknown,
  result: unknown,
): void {
  if (eventKeys.success) {
    void EventPublisher.publishEvent(eventKeys.success, {
      ...eventData,
      form_data: data,
      result,
    });
  }
}

const SERVER_ACTION_NOT_FOUND_MARKER = "was not found on the server";

function isServerActionNotFoundError(message: string): boolean {
  return message.includes(SERVER_ACTION_NOT_FOUND_MARKER);
}

const NETWORK_ERROR_DISPLAY =
  "We couldn't reach NeedBridge. Check your internet connection, then submit again.";

/** What the person sees: stale-deploy and dropped-connection errors get plain words. */
function toDisplayMessage(message: string): string {
  if (isServerActionNotFoundError(message)) {
    return "Please refresh the page and try again.";
  }
  if (isNetworkErrorMessage(message)) return NETWORK_ERROR_DISPLAY;

  return message;
}

function showErrorToast(description: string): void {
  const title = isServerActionNotFoundError(description)
    ? "Page may be out of date"
    : isNetworkErrorMessage(description)
      ? "Connection problem"
      : "Form submission failed";

  addToast({
    color: "danger",
    description: toDisplayMessage(description),
    title,
  });
}

function showSuccessToast(description: string): void {
  addToast({
    color: "success",
    description,
    title: "Form submitted successfully",
  });
}

function handleErrorResult<TData>(
  errorMessage: string,
  ctx: SubmissionContext<TData>,
): void {
  publishFailureEvent(ctx.eventKeys, ctx.eventData, ctx.data, errorMessage);
  ctx.setError(errorMessage);
  if (ctx.showToast) showErrorToast(errorMessage);
  ctx.setIsSubmitting(false);
}

function handleSuccessResult<TData>(
  result: any,
  message: string,
  ctx: SubmissionContext<TData>,
): void {
  publishSuccessEvent(ctx.eventKeys, ctx.eventData, ctx.data, result);
  if (ctx.showToast) showSuccessToast(message);
  ctx.onSuccess?.(result);
}

function isLegacyErrorResult(
  result: unknown,
): result is { success: false; error: string } {
  return (
    result !== null &&
    typeof result === "object" &&
    "success" in result &&
    (result as Record<string, unknown>).success === false &&
    "error" in result &&
    Boolean((result as Record<string, unknown>).error)
  );
}

function extractLegacySuccessMessage(result: unknown): string {
  if (typeof result === "object" && result !== null && "message" in result) {
    return String((result as Record<string, unknown>).message);
  }

  return "Success";
}

function isActionResponseObject(
  result: unknown,
): result is ActionResponse<unknown> {
  return Boolean(result) && typeof result === "object" && "success" in result!;
}

function processActionResponse<TData>(
  result: unknown,
  ctx: SubmissionContext<TData>,
): { handled: boolean; returnValue?: unknown } {
  if (!isActionResponseObject(result)) return { handled: false };

  if (isErrorResponse(result)) {
    handleErrorResult(result.error ?? "An error occurred", ctx);

    return { handled: true, returnValue: result };
  }

  if (isSuccessResponse(result)) {
    handleSuccessResult(result, result.message ?? "Success", ctx);

    return { handled: true, returnValue: result };
  }

  return { handled: false };
}

function processLegacyResult<TData>(
  result: any,
  ctx: SubmissionContext<TData>,
): { handled: boolean; returnValue?: any } {
  if (isLegacyErrorResult(result)) {
    const errorMessage = String(result.error);

    handleErrorResult(errorMessage, ctx);

    return { handled: true, returnValue: result };
  }

  const message = extractLegacySuccessMessage(result);

  handleSuccessResult(result, message, ctx);

  return { handled: true, returnValue: result };
}

function useFormSubmission<
  TData,
  TResult extends ActionResponse<any> = ActionResponse<any>,
>({
  eventData = {},
  eventKeys,
  onBeforeSubmit,
  onError,
  onSubmit,
  onSuccess,
  resetErrorOnSubmit = true,
  showToast = true,
}: UseFormSubmissionOptions<TData, TResult>): UseFormSubmissionResult<
  TData,
  TResult
> {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = () => setError(null);

  function publishAttemptEvent(data: TData): void {
    if (eventKeys.attempt) {
      void EventPublisher.publishEvent(eventKeys.attempt, {
        ...eventData,
        form_data: data,
      });
    }
  }

  function handleCaughtError(err: unknown, data: TData): void {
    const errorMessage =
      err instanceof Error ? err.message : "An unexpected error occurred";
    const displayMessage = toDisplayMessage(errorMessage);

    publishFailureEvent(eventKeys, eventData, data, errorMessage);
    setError(displayMessage);
    if (showToast) showErrorToast(errorMessage);
    if (err instanceof Error) onError?.(err);
  }

  const handleSubmit = async (data: TData): Promise<TResult | undefined> => {
    publishAttemptEvent(data);
    onBeforeSubmit?.();
    setIsSubmitting(true);
    if (resetErrorOnSubmit) clearError();

    const ctx: SubmissionContext<TData> = {
      data,
      eventData,
      eventKeys,
      onError,
      onSuccess,
      setError,
      setIsSubmitting,
      showToast,
    };

    try {
      const result = await onSubmit(data);
      const actionResponse = processActionResponse(result, ctx);

      if (actionResponse.handled) return actionResponse.returnValue as TResult;

      const legacyResponse = processLegacyResult(result, ctx);

      return legacyResponse.returnValue as TResult;
    } catch (err) {
      handleCaughtError(err, data);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    clearError,
    error,
    handleSubmit,
    isSubmitting,
    setError,
  };
}

/**
 * Shared hook for unified form template logic (setup useForm and useFormSubmission)
 */
export function useUnifiedFormTemplate<
  TFormValues extends FieldValues,
  TSchema extends z.ZodType<TFormValues>,
>({
  defaultValues,
  eventData = {},
  eventKeys,
  formSchema,
  onError,
  onSubmit,
  onSuccess,
}: {
  defaultValues: any;
  eventData?: Record<string, any>;
  eventKeys: {
    attempt: any;
    success: any;
    failure: any;
  };
  formSchema: TSchema;
  onError?: (error: Error) => void;
  onSubmit: (data: z.infer<TSchema>) => Promise<any>;
  onSuccess?: (result: any) => void;
}) {
  const {
    control,
    formState,
    handleSubmit: hookFormSubmit,
    reset,
    setValue,
    trigger,
    watch,
  } = useForm<TFormValues>({
    defaultValues,
    resolver: zodResolver(formSchema as any),
  });

  const { error, handleSubmit, isSubmitting } = useFormSubmission<any>({
    eventData,
    eventKeys,
    onError,
    onSubmit,
    onSuccess,
    showToast: true,
  });

  return {
    control,
    error,
    formState,
    handleSubmit,
    hookFormSubmit,
    isSubmitting,
    reset,
    setValue,
    trigger,
    watch,
  };
}
